import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { asBuilderCondition, type FollowUpAnswer } from '@lib/customization/poiDesign';
import { type DraftIssue } from '@lib/customization/poiDraft';
import {
  conditionComplete, conditionProblem, OPERATOR_VALUE_KIND, type RuleSubjectType,
} from '@lib/customization/poiRules';
import { LocalizationManager } from '@lib/localization';
import {
  type LocalizedText, type PoiField, type PoiFieldCondition, type PoiFieldGroup, type PoiFieldRule, type RuleOperator,
} from '@store/api/poiTypeEndpoints';

/**
 * Rules in words, for the canvas, the builder and the inspector. Every sentence is one key with
 * placeholders, never words glued together, since the order of the parts differs by language.
 */

const { t } = LocalizationManager;

type Resolve = (text?: LocalizedText) => string;
type Candidates = ReadonlyMap<string, RuleCandidate>;

/** How an operator reads after a subject of this type: the builder's menu item, and the sentence it makes. */
const phrasing = (operator: RuleOperator, type?: RuleSubjectType): { menu: string; sentence: string } => {
  const multi = type === 'MultiList';
  const text = type === 'Text' || type === 'Barcode';
  const keys = (menu: string, sentence: string) => ({ menu, sentence });

  switch (operator) {
    case 'equals': return multi ? keys('Customization_Operator_isExactly', 'Customization_Designer_Cond_IsExactly') : keys('Customization_Operator_equals', 'Customization_Designer_Cond_Is');
    case 'notEquals': return keys('Customization_Operator_notEquals', 'Customization_Designer_Cond_IsNot');
    case 'contains': return keys('Customization_Operator_contains', 'Customization_Designer_Cond_Contains');
    case 'notContains': return keys('Customization_Operator_notContains', 'Customization_Designer_Cond_NotContains');
    case 'isAnyOf': return multi ? keys('Customization_Operator_includes', 'Customization_Designer_Cond_Includes') : keys('Customization_Operator_equals', 'Customization_Designer_Cond_Is');
    case 'isNoneOf': return multi ? keys('Customization_Operator_includesNone', 'Customization_Designer_Cond_IncludesNone') : keys('Customization_Operator_notEquals', 'Customization_Designer_Cond_IsNot');
    case 'gt': return keys('Customization_Operator_gt', 'Customization_Designer_Cond_Gt');
    case 'gte': return keys('Customization_Operator_gte', 'Customization_Designer_Cond_Gte');
    case 'lt': return keys('Customization_Operator_lt', 'Customization_Designer_Cond_Lt');
    case 'lte': return keys('Customization_Operator_lte', 'Customization_Designer_Cond_Lte');
    case 'countEquals': return keys('Customization_Operator_countEquals', 'Customization_Designer_Cond_CountEquals');
    case 'countGt': return keys('Customization_Operator_countGt', 'Customization_Designer_Cond_CountGt');
    case 'countLt': return keys('Customization_Operator_countLt', 'Customization_Designer_Cond_CountLt');
    case 'isEmpty': return text ? keys('Customization_Operator_isEmpty', 'Customization_Designer_Cond_Empty') : keys('Customization_Operator_unanswered', 'Customization_Designer_Cond_Unanswered');
    case 'isNotEmpty': return text ? keys('Customization_Operator_isNotEmpty', 'Customization_Designer_Cond_Filled') : keys('Customization_Operator_answered', 'Customization_Designer_Cond_Answered');
  }
};

/** An operator as the builder's menu lists it: "is", "includes", "is answered". */
export const operatorWords = (operator: RuleOperator, type?: RuleSubjectType): string => t(phrasing(operator, type).menu);

/** "A, B or C"; "or" on choices means any one of them. */
const list = (names: string[], match: 'all' | 'any') => names.length === 1
  ? names[0]
  : t(match === 'any' ? 'Customization_Designer_JoinOr' : 'Customization_Designer_JoinAnd', [names.slice(0, -1).join(', '), names[names.length - 1]]);

/** The value half of a condition: "Body side or Floor", "10", "“crack”". */
const valueWords = (condition: PoiFieldCondition, candidate: RuleCandidate | undefined, resolve: Resolve): string => {
  const kind = OPERATOR_VALUE_KIND[condition.operator];
  const { value } = condition;
  const choice = (id: string) => {
    const option = candidate?.options?.find((entry) => entry.id === id);

    return option ? resolve(option.label) || id : t('Customization_Designer_MissingOption');
  };

  if (kind === 'none') return '';
  if (candidate?.type === 'Toggle') return t(value === false ? 'Customization_Designer_No' : 'Customization_Designer_Yes');
  if (typeof value === 'number') return String(value);
  if (candidate?.type === 'List' && typeof value === 'string') return value ? choice(value) : '…';
  if (typeof value === 'string') return `“${value}”`;
  if (!Array.isArray(value) || !value.length) return '…';

  // "Is any of" reads with "or" on both choice types: on a MultiList it means at least one
  // of them is ticked, never all of them (contract §4).
  return list(value.map(choice), condition.operator === 'isAnyOf' ? 'any' : 'all');
};

/** One condition as a sentence: "Location is Body side". */
const conditionWords = (stored: PoiFieldCondition, candidates: Candidates, resolve: Resolve): string => {
  const candidate = candidates.get(stored.fieldId);

  if (!candidate) return t('Customization_Designer_DeletedField');

  const condition = asBuilderCondition(stored, candidate);

  return t(phrasing(condition.operator, candidate.type).sentence, [candidate.label, valueWords(condition, candidate, resolve)]);
};

/** A rule's `when` as a sentence: "Location is Body side and …". */
export const whenWords = (when: PoiFieldRule['when'], candidates: Candidates, resolve: Resolve): string =>
  list(when.conditions.map((condition) => conditionWords(condition, candidates, resolve)), when.match);

/**
 * The label over a run of follow-ups. Under a dropdown or a switch the answer alone says it:
 * "Body side" reads better than "Location is Body side" right under Location. Anything else
 * keeps its whole sentence.
 */
export const branchWords = (when: PoiFieldRule['when'], candidates: Candidates, resolve: Resolve): string =>
  list(when.conditions.map((stored) => {
    const candidate = candidates.get(stored.fieldId);
    const condition = asBuilderCondition(stored, candidate);
    const answerOnly = candidate?.type === 'Toggle' || (candidate?.type === 'List' && condition.operator === 'isAnyOf');

    return answerOnly ? valueWords(condition, candidate, resolve) : conditionWords(stored, candidates, resolve);
  }), when.match);

/** A rule's `then` in words: "shown, required, not on reports". */
const thenWords = (then: PoiFieldRule['then']): string => Object.entries(then)
  .filter(([, value]) => value !== undefined)
  .map(([key, value]) => {
    switch (key) {
      case 'visible': return t(value ? 'Customization_Designer_Effect_Shown' : 'Customization_Designer_Effect_Hidden');
      case 'required': return t(value ? 'Customization_Designer_Effect_Required' : 'Common_Optional');
      case 'readOnly': return t(value ? 'Customization_Designer_Effect_ReadOnly' : 'Customization_Designer_Effect_Editable');
      default: return t(value ? 'Customization_Designer_Effect_OnReports' : 'Customization_Designer_Effect_OffReports');
    }
  })
  .join(', ');

/** A whole rule in one line: "When Location is Floor: shown, required". */
export const ruleWords = (rule: PoiFieldRule, candidates: Candidates, resolve: Resolve): string =>
  t('Customization_Designer_RuleLine', [whenWords(rule.when, candidates, resolve), thenWords(rule.then)]);

/** Legal but surely not meant: hidden with nothing to show it, or a condition gone stale. */
export const entryWarnings = (entry: PoiField | PoiFieldGroup, candidates: Candidates): string[] => {
  const rules = entry.rules ?? [];
  // A condition still being written is not stale; it is reported as incomplete instead.
  const stale = rules.some((rule) => rule.when.conditions
    .some((condition) => conditionComplete(condition) && conditionProblem(condition, candidates.get(condition.fieldId))));

  return [
    ...(entry.visible === false && !rules.some((rule) => rule.then.visible) ? [t('Customization_Designer_NeverShown')] : []),
    ...(stale ? [t('Customization_Designer_BrokenCondition')] : []),
  ];
};

/** A follow-up answer as the author sees it: the choice's name, Yes, "above a value"… */
export const answerLabel = (answer: FollowUpAnswer, resolve: Resolve): string => {
  if (answer.option) return resolve(answer.option.label) || '…';

  switch (answer.key) {
    case 'yes': return t('Customization_Designer_Yes');
    case 'no': return t('Customization_Designer_No');
    case 'above': return t('Customization_Designer_AboveValue');
    default: return t('Customization_Designer_FilledIn');
  }
};

const ISSUE_KEYS = {
  missing: 'Customization_Designer_IssueMissingField',
  operator: 'Customization_Designer_IssueWrongOperator',
  option: 'Customization_Designer_IssueMissingChoice',
} as const;

/** A save-blocking issue that needs words of its own: a condition the save would prune. */
export const issueWords = (issue: DraftIssue, candidates: Candidates, resolve: Resolve): string | null => {
  if (!('condition' in issue)) return null;

  const where = resolve('label' in issue.entry ? issue.entry.label : issue.entry.name) || issue.entry.id || '?';
  const field = candidates.get(issue.condition.fieldId)?.label ?? issue.condition.fieldId;

  return t(ISSUE_KEYS[issue.problem], [where, field, issue.condition.operator]);
};
